import cx from 'classnames'
import styles from './LevelMeter.module.css'

export interface LevelMeterProps {
  /** Signal level between 0 and 1 */
  level: number
  /** Label shown under the meter */
  label?: string
  /** Number of LED segments */
  segments?: number
  /** Segment index (from the bottom) where the hot (yellow) zone starts */
  hotFrom?: number
  /** Segment index (from the bottom) where the clip (pink) zone starts */
  clipFrom?: number
  /** Accessible label for the meter */
  ariaLabel?: string
  /** Additional CSS class */
  className?: string
}

/**
 * A segmented LED level meter running green through yellow
 * to pink across the status colour scale.
 */
export function LevelMeter({
  level,
  label,
  segments = 12,
  hotFrom = 7,
  clipFrom = 10,
  ariaLabel,
  className,
}: LevelMeterProps) {
  const clamped = Math.min(1, Math.max(0, level))
  const litCount = Math.round(clamped * segments)

  return (
    <div
      className={cx(styles.LevelMeter, className)}
      role="meter"
      aria-label={ariaLabel ?? label ?? 'Level meter'}
      aria-valuemin={0}
      aria-valuemax={1}
      aria-valuenow={Number(clamped.toFixed(2))}
    >
      <div className={styles.LevelMeter__column}>
        {Array.from({ length: segments }, (_, index) => (
          <div
            key={index}
            className={cx(styles.LevelMeter__segment, {
              [styles.isHot]:
                index >= hotFrom && index < clipFrom,
              [styles.isClip]: index >= clipFrom,
              [styles.isLit]: index < litCount,
            })}
          />
        ))}
      </div>
      {label && (
        <span className={styles.LevelMeter__label}>
          {label}
        </span>
      )}
    </div>
  )
}
